import cx from 'clsx'
import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react'
import AliceCarousel from 'react-alice-carousel'

import s from './styles.module.scss'

import ProposalVoteCounters from './proposal-vote-counters/ProposalVoteCounters'
import { HousePictureFieldsFragment } from '../../../../lib/graphql'
import { breakpoints, useMediaQuery } from '../../../../lib/hooks'
import { IconSvg, Image, NextImage, TagAutonomousBooking } from '../../../index'

interface IProposalGalleryProps {
  negativeVoteCount: number
  positiveVoteCount: number
  housePictures: Omit<HousePictureFieldsFragment, '__typename'>[]
  onClickImage?: () => void
  itemContainerClassName?: string
  mainCarouselClassName?: string
  isAutonomousBookingAvailable?: boolean
  placeholder?: ReactNode
}

const Item = ({
  uri,
  onClick = undefined,
  contain = false,
  itemContainerClassName,
}: {
  uri: string
  onClick?: () => void
  contain: boolean
  itemContainerClassName?: string
}) => {
  // cf: https://linear.app/naboo-team/issue/RND-531/qualite-dimage-mediocre-pour-le-chateau-de-villiers-dans-les-resultats
  const [isPanoramicImage, setIsPanoramicImage] = useState(false)

  const onLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      const img = e.currentTarget
      if (img.naturalHeight < 230) {
        setIsPanoramicImage(true)
      }
    },
    [],
  )

  const sizes = isPanoramicImage
    ? '(max-width: 768px) 100vw, 70vw'
    : '(max-width: 768px) 85vw, 21vw'

  return (
    <div
      className={cx(s.item, { [s.itemClick]: onClick })}
      onClick={() => onClick && onClick()}
    >
      {contain ? (
        <Image alt='' src={uri} type='cover' />
      ) : (
        <div
          className={
            itemContainerClassName ? itemContainerClassName : s.itemContainer
          }
        >
          <NextImage src={uri} alt='' sizes={sizes} onLoad={onLoad} />
        </div>
      )}
    </div>
  )
}

const ProposalGallery = ({
  positiveVoteCount,
  negativeVoteCount,
  housePictures,
  onClickImage,
  itemContainerClassName,
  mainCarouselClassName,
  isAutonomousBookingAvailable,
  placeholder,
}: IProposalGalleryProps) => {
  const isMobile = useMediaQuery(breakpoints.newMobile)
  const pictures = useMemo(
    () => housePictures.map((housePicture) => housePicture.uri),
    [housePictures],
  )

  const items = pictures.map((pic, idx) => (
    <Item
      key={idx.toString()}
      uri={pic}
      contain={false}
      onClick={onClickImage}
      itemContainerClassName={itemContainerClassName}
    />
  ))

  const carousel = useRef<AliceCarousel>(null)

  return (
    <div
      className={s.gallery}
      onClick={(e) => {
        return e.stopPropagation()
      }}
    >
      <div
        className={cx(
          mainCarouselClassName ? mainCarouselClassName : s.mainCarousel,
          {
            [s.mainCarouselMobile]: isMobile,
          },
        )}
      >
        {pictures.length ? (
          <>
            <ProposalVoteCounters
              {...{ positiveVoteCount, negativeVoteCount }}
            />
            <AliceCarousel
              animationType='fadeout'
              infinite
              disableButtonsControls={isMobile}
              items={items}
              ref={carousel}
              responsive={{
                0: {
                  items: 1,
                },
              }}
              renderDotsItem={({ isActive }) => {
                const nbItems = carousel?.current?.props?.items?.length ?? 0
                const activeIndex =
                  carousel?.current?.state?.activeIndex === undefined ||
                  carousel?.current?.state?.activeIndex < 0
                    ? nbItems - 1
                    : carousel?.current?.state?.activeIndex

                const offsetRadiusNbItemsLessThan5 =
                  nbItems >= 5 ? 0 : 5 - nbItems

                const defaultOffset = offsetRadiusNbItemsLessThan5 * 8

                const offset =
                  activeIndex < 2 || nbItems - activeIndex < 1
                    ? 0
                    : nbItems - activeIndex === 2
                      ? activeIndex - 3 + offsetRadiusNbItemsLessThan5
                      : nbItems - activeIndex === 1
                        ? activeIndex - 4 + offsetRadiusNbItemsLessThan5
                        : activeIndex - 2

                return (
                  <div
                    className={s.dotsItem}
                    style={{
                      opacity: isActive ? '100%' : '80%',
                      backgroundColor: isActive ? 'white' : 'lightgrey',
                      position: 'relative',
                      transform: `translateX(${-16 * offset + defaultOffset}px)`,
                      transition: 'transform 0.2s',
                    }}
                  />
                )
              }}
              renderPrevButton={() => {
                return <IconSvg.ArrowLeft className={s.circleLeft} />
              }}
              renderNextButton={() => {
                return <IconSvg.ArrowRight className={s.circleRight} />
              }}
            />

            <div className={s.autonomousBookingContainer}>
              {isAutonomousBookingAvailable && (
                <TagAutonomousBooking className={s.autonomousTag} />
              )}
            </div>
          </>
        ) : placeholder ? (
          placeholder
        ) : null}
      </div>
    </div>
  )
}

export default ProposalGallery
